import { ComponentCO2 } from '@acies/domain';
import { FormInputEvent } from '@acies/ui';
import ConflictError from '@shared/errors/conflict-error';
import NetworkError from '@shared/errors/network-error';
import CO2Model from '../models/co2-model';
import CreateComponentCO2Model from '../models/create-component-co2-model';
import ListComponentCO2Model from '../models/list-component-co2-model';
import CO2Service from '../services/co2-service';

/**
 * The CreateComponentCO2Controller is responsible for creating new ComponentCO2's.
 *
 * When creating ComponentCO2's, the "name" property will show as "key" in the UI and will be used as the unique key created with the API.
 */
export default class CreateComponentCO2Controller {
	public dialog: HTMLDialogElement | null;
	#listComponentCO2Model: ListComponentCO2Model;
	#createComponentCO2Model: CreateComponentCO2Model;
	#co2Service: CO2Service;
	public constructor(co2Model: CO2Model, co2Service: CO2Service) {
		this.dialog = null;
		this.#listComponentCO2Model = co2Model.listComponentCO2Model;
		this.#createComponentCO2Model = co2Model.createComponentCO2Model;
		this.#co2Service = co2Service;
	}

	public closeDialog(): void {
		if (this.dialog) {
			this.dialog.close();
			this.#hideAllErrorLabels();
			this.#createComponentCO2Model.resetUIBindings();
		}
	}

	public createButtonClicked(): void {
		this.#createComponentCO2Model.createSpinnerVisible = true;
		this.#hideAllErrorLabels();
		this.#co2Service.createComponentCO2(this.#createComponentCO2Model.createComponentCO2Body).subscribe((response) => {
			this.#createComponentCO2Model.createSpinnerVisible = false;
			// If the response is an instance of ComponentCO2,
			// we have created a new ComponentCO2 successfully.
			if (response instanceof ComponentCO2) {
				// We add the newly created ComponentCO2 instance to the collection here.
				// You could argue that the collection should be completely refreshed,
				// but I choose not to in this case.
				this.#listComponentCO2Model.co2Collection.addItem(response);
				this.closeDialog();
			} else {
				this.#showErrorLabel(response);
			}
		});
	}

	#hideAllErrorLabels(): void {
		this.#createComponentCO2Model.conflictErrorLabelVisible = false;
		this.#createComponentCO2Model.networkErrorLabelVisible = false;
		this.#createComponentCO2Model.unknownErrorLabelVisible = false;
	}

	#showErrorLabel(error: ConflictError | NetworkError): void {
		if (error instanceof ConflictError) {
			this.#createComponentCO2Model.conflictErrorLabelVisible = true;
		} else if (error instanceof NetworkError) {
			this.#createComponentCO2Model.networkErrorLabelVisible = true;
		} else {
			this.#createComponentCO2Model.unknownErrorLabelVisible = true;
		}
	}

	/** When FormInput components in the Edit Dialog dispatches events, they are forwarded to this method. Angular expects the Event type as parameter in event handlers, so we check for the event being an instance of the FormInputEvent to get full type safety and the TSC compiler happy :) */
	public formInputEvent(event: Event): void {
		if (event instanceof FormInputEvent) {
			this.#updateCreateComponentCO2ModelFromEvent(event);
			if (event.type === FormInputEvent.NOT_VALID_VALUE_CHANGED) {
				this.#createComponentCO2Model.createButtonEnabled = false;
			}
		}
	}

	public showDialog(): void {
		if (this.dialog) {
			this.dialog.showModal();
		}
	}

	#updateCreateComponentCO2ModelFromEvent(event: FormInputEvent): void {
		switch (event.key) {
			case 'epd':
				this.#createComponentCO2Model.epd = event.value;
				break;
			case 'name':
				this.#createComponentCO2Model.name = event.value;
				break;
			case 'description':
				this.#createComponentCO2Model.description = event.value;
				break;
			case 'a1':
				this.#createComponentCO2Model.a1 = event.value;
				break;
			case 'a2':
				this.#createComponentCO2Model.a2 = event.value;
				break;
			case 'a3':
				this.#createComponentCO2Model.a3 = event.value;
				break;
			case 'a4':
				this.#createComponentCO2Model.a4 = event.value;
				break;
			case 'a5':
				this.#createComponentCO2Model.a5 = event.value;
				break;
			case 'c1':
				this.#createComponentCO2Model.c1 = event.value;
				break;
			case 'c2':
				this.#createComponentCO2Model.c2 = event.value;
				break;
			case 'c3':
				this.#createComponentCO2Model.c3 = event.value;
				break;
			case 'c4':
				this.#createComponentCO2Model.c4 = event.value;
				break;
			case 'd':
				this.#createComponentCO2Model.d = event.value;
				break;
			default:
				break;
		}
		this.#updateCreateComponentCO2IsValid();
	}

	#updateCreateComponentCO2IsValid(): void {
		this.#createComponentCO2Model.createButtonEnabled = this.#createComponentCO2Model.epd !== '' && this.#createComponentCO2Model.name !== '';
	}
}
